import { useEffect, useMemo, useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const STEPS = ['Customer & Event', 'Package', 'Menu', 'Add-ons & Pricing', 'Review'];

const formatCurrency = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

function BanquetQuotationV2({ token, selectedProperty, selectedOutletId = '', leadPrefill, onLeadPrefillConsumed }) {
  const { isMobile } = useIsMobile();
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId,
    ...(outletId ? { 'x-outlet-id': outletId } : {})
  }), [token, chainId, propertyId, outletId]);

  const [view, setView] = useState('list'); // list | wizard | detail
  const [step, setStep] = useState(0);
  const [quotations, setQuotations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [halls, setHalls] = useState([]);
  const [taxOptions, setTaxOptions] = useState([]);
  const [packageFull, setPackageFull] = useState(null); // loaded package with rules + items
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [showPrint, setShowPrint] = useState(false);
  const [hotelInfo, setHotelInfo] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveHallId, setApproveHallId] = useState('');
  const [approveLayoutId, setApproveLayoutId] = useState('');
  const [approveLayouts, setApproveLayouts] = useState([]);

  // Wizard form state
  const [form, setForm] = useState({
    customer_name: '', customer_mobile: '', customer_email: '',
    event_name: '', event_date: '', event_end_date: '', event_from_time: '', event_to_time: '',
    venue: '', pax_count: '', notes: '', lead_id: null
  });
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [pricePerPax, setPricePerPax] = useState('');
  const [menuSelections, setMenuSelections] = useState([]); // [{ item_id, category_id, item_name, food_type }]
  const [addons, setAddons] = useState([]); // [{ addon_name, rate_type, rate, quantity }]
  const [discountType, setDiscountType] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [taxPercent, setTaxPercent] = useState(18);

  const parseJson = async (r) => { const t = await r.text(); try { return JSON.parse(t); } catch { return { error: t }; } };
  const flash = (msg, isErr) => { if (isErr) setError(msg); else setMessage(msg); setTimeout(() => { setMessage(''); setError(''); }, 4000); };

  // Load data
  const loadQuotations = async () => {
    const res = await fetch('/api/v1/banquet/quotations-v2', { headers });
    const data = await parseJson(res);
    if (res.ok) setQuotations(Array.isArray(data) ? data : []);
  };
  const loadPackages = async () => {
    const res = await fetch('/api/v1/banquet/packages', { headers });
    const data = await parseJson(res);
    if (res.ok) setPackages(Array.isArray(data) ? data : []);
  };
  const loadCategories = async () => {
    const res = await fetch('/api/v1/banquet/menu-categories', { headers });
    const data = await parseJson(res);
    if (res.ok) setCategories(Array.isArray(data) ? data : []);
  };
  const loadHalls = async () => {
    const res = await fetch('/api/v1/banquet/halls', { headers });
    const data = await parseJson(res);
    if (res.ok) setHalls(Array.isArray(data) ? data : []);
  };
  const loadApproveLayouts = async (hallId) => {
    if (!hallId) { setApproveLayouts([]); return; }
    try {
      const res = await fetch(`/api/v1/banquet/halls/${hallId}/layouts`, { headers });
      const data = await parseJson(res);
      if (res.ok) setApproveLayouts(Array.isArray(data) ? data : []);
    } catch {}
  };

  const approveQuotation = async () => {
    if (!approveHallId || !approveLayoutId) return flash('Select hall and layout', true);
    try {
      const res = await fetch(`/api/v1/banquet/quotations/${detailData.quotation_id}/approve`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ hall_id: Number(approveHallId), layout_id: Number(approveLayoutId), event_name: detailData.event_name || '' })
      });
      const data = await parseJson(res);
      if (!res.ok) return flash(data?.error || 'Approval failed', true);
      flash('Quotation approved — Booking created!');
      setShowApproveModal(false);
      loadDetail(detailData.quotation_id);
      loadQuotations();
    } catch (err) { flash(err?.message || 'Failed', true); }
  };

  const loadHotelInfo = async () => {
    // Load from banquet config (billing profile + tax)
    try {
      const res = await fetch('/api/v1/banquet/config', { headers });
      const data = await parseJson(res);
      if (res.ok && data) {
        if (data.billing_company_name) setHotelInfo(data);
        if (data.tax_percent) setTaxPercent(Number(data.tax_percent));
      }
    } catch {}
    // Fallback to PMS property config
    if (!hotelInfo) {
      try {
        const res = await fetch('/api/v1/pms/property-config', { headers });
        const data = await parseJson(res);
        if (res.ok) setHotelInfo(prev => prev || data);
      } catch {}
    }
  };

  useEffect(() => {
    if (chainId && propertyId) {
      setLoading(true);
      Promise.all([loadQuotations(), loadPackages(), loadCategories(), loadHalls(), loadHotelInfo()]).finally(() => setLoading(false));
    }
  }, [chainId, propertyId]);

  // Lead prefill
  useEffect(() => {
    if (!leadPrefill) return;
    if (leadPrefill.view_quotation_id) {
      loadDetail(leadPrefill.view_quotation_id);
    } else {
      startNewQuotation();
      setForm(f => ({
        ...f,
        customer_name: leadPrefill.customer_name || '',
        customer_mobile: leadPrefill.customer_phone || '',
        customer_email: leadPrefill.customer_email || '',
        event_name: leadPrefill.event_name || '',
        event_date: leadPrefill.event_start_date || '',
        event_end_date: leadPrefill.event_end_date || '',
        event_from_time: leadPrefill.event_from_time || '',
        event_to_time: leadPrefill.event_to_time || '',
        pax_count: leadPrefill.expected_pax || '',
        notes: leadPrefill.notes || '',
        venue: leadPrefill.venue || '',
        lead_id: leadPrefill.lead_id || null
      }));
    }
    if (onLeadPrefillConsumed) onLeadPrefillConsumed();
  }, [leadPrefill]);

  // Load package details when selected
  const loadPackageFull = useCallback(async (pkgId, skipDefaults = false) => {
    if (!pkgId) { setPackageFull(null); return; }
    const res = await fetch(`/api/v1/banquet/packages/${pkgId}/full`, { headers });
    const data = await parseJson(res);
    if (res.ok) {
      setPackageFull(data);
      if (!skipDefaults) {
        setPricePerPax(data.base_price_per_pax || '');
        // Pre-select all items by default — user can deselect what they don't want
        const allItems = (data.items || []).map(i => ({
          item_id: String(i.item_id), category_id: String(i.category_id),
          item_name: i.item_name, food_type: i.food_type
        }));
        setMenuSelections(allItems);
      }
    }
  }, [headers]);

  useEffect(() => { if (selectedPackageId && !editingId) loadPackageFull(selectedPackageId); }, [selectedPackageId]);

  // Price calculations
  const pax = Number(form.pax_count) || 0;
  const ppPax = Number(pricePerPax) || 0;
  const foodTotal = pax * ppPax;
  const addonTotal = addons.reduce((s, a) => {
    const r = Number(a.rate || 0);
    const q = Number(a.quantity || 1);
    return s + (a.rate_type === 'PER_PAX' ? r * pax : r * q);
  }, 0);
  const subtotal = foodTotal + addonTotal;
  const discAmt = discountType === 'PERCENT' ? subtotal * Number(discountValue || 0) / 100 : Number(discountValue || 0);
  const taxable = subtotal - discAmt;
  const taxAmt = taxable * Number(taxPercent) / 100;
  const grandTotal = taxable + taxAmt;

  const startNewQuotation = () => {
    setForm({ customer_name: '', customer_mobile: '', customer_email: '', event_name: '', event_date: '', event_end_date: '', event_time: '', venue: '', pax_count: '', notes: '', lead_id: null });
    setSelectedPackageId(''); setPricePerPax(''); setMenuSelections([]); setAddons([]);
    setDiscountType(''); setDiscountValue(''); setPackageFull(null);
    setEditingId(null); setStep(0); setView('wizard');
  };

  const loadDetail = async (id) => {
    const res = await fetch(`/api/v1/banquet/quotations-v2/${id}`, { headers });
    const data = await parseJson(res);
    if (res.ok) { setDetailData(data); setView('detail'); }
    else flash(data?.error || 'Failed to load', true);
  };

  const editQuotation = async (id) => {
    const res = await fetch(`/api/v1/banquet/quotations-v2/${id}`, { headers });
    const data = await parseJson(res);
    if (!res.ok) return flash(data?.error || 'Failed', true);
    setForm({
      customer_name: data.customer_name || '', customer_mobile: data.customer_phone || '',
      customer_email: data.customer_email || '', event_name: data.event_name || '',
      event_date: data.event_start_date ? data.event_start_date.slice(0, 10) : '',
      event_end_date: data.event_end_date ? data.event_end_date.slice(0, 10) : '',
      event_time: data.event_time || '', venue: data.venue || '',
      pax_count: data.expected_pax || '', notes: data.notes || '', lead_id: data.lead_id || null
    });
    setSelectedPackageId(String(data.package_id || ''));
    setPricePerPax(data.price_per_pax || '');
    setDiscountType(data.discount_type || '');
    setDiscountValue(data.discount_value || '');
    setTaxPercent(data.tax_percent || 18);
    const savedSelections = (data.menu_selections || []).map(s => ({
      item_id: String(s.item_id), category_id: String(s.category_id),
      item_name: s.item_name, food_type: s.food_type
    }));
    setMenuSelections(savedSelections);
    setAddons((data.addons || []).map(a => ({ addon_name: a.addon_name, rate_type: a.rate_type, rate: a.rate, quantity: a.quantity })));
    setEditingId(id); setStep(0); setView('wizard');
    // If no saved selections, load package defaults; otherwise just load package structure
    if (data.package_id) loadPackageFull(data.package_id, savedSelections.length > 0);
  };

  const duplicateQuotation = async (id) => {
    const res = await fetch(`/api/v1/banquet/quotations-v2/${id}/duplicate`, { method: 'POST', headers });
    const data = await parseJson(res);
    if (res.ok) { flash(`Duplicated as ${data.quotation_no}`); loadQuotations(); }
    else flash(data?.error || 'Failed', true);
  };

  const saveQuotation = async () => {
    if (!form.event_date || !form.pax_count || !selectedPackageId) return flash('Event date, pax count and package are required', true);

    // Validate menu rules
    if (packageFull?.rules) {
      for (const rule of packageFull.rules) {
        const count = menuSelections.filter(s => String(s.category_id) === String(rule.category_id)).length;
        if (rule.min_select > 0 && count < rule.min_select) {
          return flash(`${rule.category_name}: select at least ${rule.min_select} items (have ${count})`, true);
        }
        if (rule.max_select > 0 && count > rule.max_select) {
          return flash(`${rule.category_name}: max ${rule.max_select} items allowed (have ${count})`, true);
        }
      }
    }

    const payload = {
      ...form, customer_phone: form.customer_mobile, pax_count: Number(form.pax_count),
      event_time: form.event_from_time || null,
      event_end_time: form.event_to_time || null,
      package_id: Number(selectedPackageId), price_per_pax: ppPax,
      discount_type: discountType || null, discount_value: Number(discountValue || 0), tax_percent: taxPercent,
      menu_selections: menuSelections.map(s => ({ item_id: Number(s.item_id), category_id: Number(s.category_id), item_price: 0, is_extra: false })),
      addons: addons.filter(a => a.addon_name)
    };

    const isEdit = !!editingId;
    const res = await fetch(isEdit ? `/api/v1/banquet/quotations-v2/${editingId}` : '/api/v1/banquet/quotations-v2', {
      method: isEdit ? 'PUT' : 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
    });
    const data = await parseJson(res);
    if (!res.ok) return flash(data?.error || 'Failed to save', true);
    flash(isEdit ? 'Quotation updated' : `Quotation ${data.quotation_no} created`);
    setView('list'); loadQuotations();
  };

  const changeStatus = async (id, status) => {
    const res = await fetch(`/api/v1/banquet/quotations-v2/${id}/status`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ status })
    });
    if (res.ok) { flash(`Status changed to ${status}`); loadQuotations(); if (detailData?.quotation_id == id) loadDetail(id); }
    else { const d = await parseJson(res); flash(d?.error || 'Failed', true); }
  };

  // Toggle menu item selection
  const toggleMenuItem = (item) => {
    const key = String(item.item_id);
    const exists = menuSelections.find(s => s.item_id === key);
    if (exists) setMenuSelections(menuSelections.filter(s => s.item_id !== key));
    else setMenuSelections([...menuSelections, { item_id: key, category_id: String(item.category_id), item_name: item.item_name, food_type: item.food_type }]);
  };

  // Styles
  const inputStyle = { padding: '0.6rem 0.85rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.88rem', width: '100%', background: '#f8fafc', transition: 'border 0.2s, box-shadow 0.2s', outline: 'none' };
  const btnPrimary = { padding: '0.6rem 1.6rem', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, boxShadow: '0 2px 8px rgba(37,99,235,0.3)', transition: 'transform 0.15s, box-shadow 0.15s' };
  const btnOutline = { padding: '0.55rem 1.2rem', border: '1.5px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontSize: '0.84rem', background: '#fff', color: '#475569', fontWeight: 500, transition: 'all 0.15s' };
  const btnSuccess = { ...btnPrimary, background: 'linear-gradient(135deg, #16a34a, #15803d)', boxShadow: '0 2px 8px rgba(22,163,74,0.3)' };

  const statusBadge = (s) => {
    const colors = { DRAFT: '#6b7280', SENT: '#2563eb', CONFIRMED: '#16a34a', CANCELLED: '#dc2626', APPROVED: '#16a34a' };
    return <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '10px', background: `${colors[s] || '#6b7280'}15`, color: colors[s] || '#6b7280', fontWeight: 600 }}>{s}</span>;
  };

  // ──── Summary Panel (right side) ────
  const SummaryPanel = () => (
    <div style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '1.25rem', position: 'sticky', top: '1rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
      <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <span style={{ display: 'inline-block', width: '4px', height: '18px', borderRadius: '2px', background: 'linear-gradient(180deg, #2563eb, #7c3aed)' }}></span>
        Quotation Summary
      </h3>
      {form.customer_name && <p style={{ fontSize: '0.82rem', marginBottom: '0.3rem' }}><strong>Customer:</strong> {form.customer_name}</p>}
      {form.event_name && <p style={{ fontSize: '0.82rem', marginBottom: '0.3rem' }}><strong>Event:</strong> {form.event_name}</p>}
      {form.event_date && <p style={{ fontSize: '0.82rem', marginBottom: '0.3rem' }}><strong>Date:</strong> {formatDate(form.event_date)}{form.event_from_time ? ` | ${form.event_from_time}` : ''}{form.event_to_time ? ` - ${form.event_to_time}` : ''}</p>}
      {pax > 0 && <p style={{ fontSize: '0.82rem', marginBottom: '0.3rem' }}><strong>Pax:</strong> {pax}</p>}
      {packageFull && <p style={{ fontSize: '0.82rem', marginBottom: '0.3rem' }}><strong>Package:</strong> {packageFull.package_name}</p>}

      {menuSelections.length > 0 && (
        <div style={{ marginTop: '0.75rem', borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>
          <p style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.4rem', color: '#475569' }}>Menu Items ({menuSelections.length})</p>
          {categories.map(cat => {
            const catItems = menuSelections.filter(s => String(s.category_id) === String(cat.category_id));
            if (!catItems.length) return null;
            return (
              <div key={cat.category_id} style={{ marginBottom: '0.4rem' }}>
                <p style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>{cat.category_name}</p>
                {catItems.map(i => (
                  <p key={i.item_id} style={{ fontSize: '0.78rem', paddingLeft: '0.5rem', color: '#334155' }}>
                    <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: i.food_type === 'VEG' ? '#16a34a' : '#dc2626', marginRight: '0.4rem' }}></span>
                    {i.item_name}
                  </p>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {addons.filter(a => a.addon_name).length > 0 && (
        <div style={{ marginTop: '0.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '0.5rem' }}>
          <p style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>Add-ons</p>
          {addons.filter(a => a.addon_name).map((a, i) => (
            <p key={i} style={{ fontSize: '0.78rem', display: 'flex', justifyContent: 'space-between' }}>
              <span>{a.addon_name}</span>
              <span>{formatCurrency(a.rate_type === 'PER_PAX' ? Number(a.rate) * pax : Number(a.rate) * Number(a.quantity || 1))}</span>
            </p>
          ))}
        </div>
      )}

      <div style={{ marginTop: '0.75rem', borderTop: '2px solid #cbd5e1', paddingTop: '0.75rem', fontSize: '0.82rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}><span>Food ({pax} x {formatCurrency(ppPax)})</span><span>{formatCurrency(foodTotal)}</span></div>
        {addonTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}><span>Add-ons</span><span>{formatCurrency(addonTotal)}</span></div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem', fontWeight: 600 }}><span>Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
        {discAmt > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem', color: '#dc2626' }}><span>Discount</span><span>-{formatCurrency(discAmt)}</span></div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}><span>Tax ({taxPercent}%)</span><span>{formatCurrency(taxAmt)}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1.05rem', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '2px solid #1e293b', color: '#fff', background: 'linear-gradient(135deg, #1e293b, #334155)', margin: '0.5rem -1.25rem -1.25rem', padding: '0.75rem 1.25rem', borderRadius: '0 0 14px 14px' }}>
          <span>Grand Total</span><span>{formatCurrency(grandTotal)}</span>
        </div>
      </div>
    </div>
  );

  // ──── LIST VIEW ────
  if (view === 'list') return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 700, margin: 0 }}>Banquet Quotations</h2>
        <button style={btnPrimary} onClick={startNewQuotation}>+ New Quotation</button>
      </div>
      {message && <div style={{ padding: '0.5rem 1rem', background: '#dcfce7', color: '#166534', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>{message}</div>}
      {error && <div style={{ padding: '0.5rem 1rem', background: '#fef2f2', color: '#991b1b', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
        <thead><tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
          <th style={{ padding: '0.6rem', textAlign: 'left' }}>Quotation No</th>
          <th style={{ padding: '0.6rem', textAlign: 'left' }}>Customer</th>
          <th style={{ padding: '0.6rem', textAlign: 'left' }}>Event</th>
          <th style={{ padding: '0.6rem', textAlign: 'center' }}>Pax</th>
          <th style={{ padding: '0.6rem', textAlign: 'left' }}>Package</th>
          <th style={{ padding: '0.6rem', textAlign: 'center' }}>Status</th>
          <th style={{ padding: '0.6rem', textAlign: 'right' }}>Total</th>
          <th style={{ padding: '0.6rem', textAlign: 'center' }}>Actions</th>
        </tr></thead>
        <tbody>{quotations.map(q => (
          <tr key={q.quotation_id} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }} onClick={() => loadDetail(q.quotation_id)}>
            <td style={{ padding: '0.6rem', fontWeight: 600, color: '#2563eb' }}>{q.quotation_no}</td>
            <td style={{ padding: '0.6rem' }}>{q.customer_name}</td>
            <td style={{ padding: '0.6rem' }}>{q.event_name || '-'}<br/><span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{formatDate(q.event_start_date)}</span></td>
            <td style={{ padding: '0.6rem', textAlign: 'center' }}>{q.expected_pax}</td>
            <td style={{ padding: '0.6rem' }}>{q.package_name || '-'}</td>
            <td style={{ padding: '0.6rem', textAlign: 'center' }}>{statusBadge(q.status)}</td>
            <td style={{ padding: '0.6rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(q.grand_total)}</td>
            <td style={{ padding: '0.6rem', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
              {['DRAFT', 'SENT'].includes(q.status) && <button style={{ ...btnOutline, marginRight: '0.3rem' }} onClick={() => editQuotation(q.quotation_id)}>Edit</button>}
              <button style={btnOutline} onClick={() => duplicateQuotation(q.quotation_id)}>Duplicate</button>
            </td>
          </tr>
        ))}</tbody>
      </table>
      {!quotations.length && !loading && <p style={{ textAlign: 'center', color: '#9ca3af', padding: '3rem' }}>No quotations yet. Click "+ New Quotation" to create one.</p>}
    </div>
  );

  // ──── DETAIL VIEW ────
  if (view === 'detail' && detailData) {
    const d = detailData;
    return (
      <div style={{ padding: '1.5rem', maxWidth: '900px', margin: '0 auto' }}>
        <button style={{ ...btnOutline, marginBottom: '1rem' }} onClick={() => { setView('list'); setDetailData(null); }}>← Back to List</button>
        {message && <div style={{ padding: '0.5rem 1rem', background: '#dcfce7', color: '#166534', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>{message}</div>}
        {error && <div style={{ padding: '0.5rem 1rem', background: '#fef2f2', color: '#991b1b', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>}

        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div><h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: 0 }}>{d.quotation_no}</h2><p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>{formatDate(d.created_at)}</p></div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {statusBadge(d.status)}
              {['DRAFT', 'SENT'].includes(d.status) && <button style={btnOutline} onClick={() => editQuotation(d.quotation_id)}>Edit</button>}
              <button style={{ ...btnOutline, color: '#7c3aed', borderColor: '#c4b5fd' }} onClick={() => setShowPrint(true)}>Preview / Print</button>
              {['DRAFT', 'SENT'].includes(d.status) && <button style={{ ...btnPrimary, background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }} onClick={() => changeStatus(d.quotation_id, 'SENT')}>{d.status === 'SENT' ? 'Re-send' : 'Mark Sent'}</button>}
              {d.status === 'SENT' && <button style={btnSuccess} onClick={() => changeStatus(d.quotation_id, 'CONFIRMED')}>Confirm</button>}
              {d.status === 'CONFIRMED' && <button style={{ ...btnPrimary, background: 'linear-gradient(135deg, #ea580c, #c2410c)', boxShadow: '0 2px 8px rgba(234,88,12,0.3)' }} onClick={() => setShowApproveModal(true)}>Convert to Booking</button>}
              {!['CANCELLED', 'CONFIRMED', 'APPROVED'].includes(d.status) && <button style={{ ...btnOutline, color: '#dc2626', borderColor: '#fca5a5' }} onClick={() => changeStatus(d.quotation_id, 'CANCELLED')}>Cancel</button>}
              <button style={btnOutline} onClick={() => duplicateQuotation(d.quotation_id)}>Duplicate</button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem', fontSize: '0.85rem' }}>
            <div><p style={{ color: '#6b7280', fontSize: '0.75rem' }}>Customer</p><p style={{ fontWeight: 600 }}>{d.customer_name}</p>{d.customer_phone && <p>{d.customer_phone}</p>}{d.customer_email && <p>{d.customer_email}</p>}</div>
            <div><p style={{ color: '#6b7280', fontSize: '0.75rem' }}>Event</p><p style={{ fontWeight: 600 }}>{d.event_name || '-'}</p><p>{formatDate(d.event_start_date)}{d.event_time ? ` | ${d.event_time}` : ''}</p>{d.venue && <p>{d.venue}</p>}<p>Pax: {d.expected_pax}</p></div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Package: {d.package_name || '-'}</p>
            {(d.menu_selections || []).length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                {categories.map(cat => {
                  const items = (d.menu_selections || []).filter(s => String(s.category_id) === String(cat.category_id));
                  if (!items.length) return null;
                  return (
                    <div key={cat.category_id} style={{ background: '#f8fafc', borderRadius: '8px', padding: '0.75rem' }}>
                      <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.3rem' }}>{cat.category_name}</p>
                      {items.map(i => (
                        <p key={i.item_id} style={{ fontSize: '0.82rem', marginBottom: '0.15rem' }}>
                          <span style={{ display: 'inline-block', width: '7px', height: '7px', borderRadius: '50%', background: i.food_type === 'VEG' ? '#16a34a' : '#dc2626', marginRight: '0.4rem' }}></span>
                          {i.item_name}
                        </p>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {(d.addons || []).length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Add-ons</p>
              {d.addons.map((a, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.2rem' }}>
                  <span>{a.addon_name} ({a.rate_type === 'PER_PAX' ? 'Per Pax' : 'Fixed'})</span>
                  <span>{formatCurrency(a.amount)}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ borderTop: '2px solid #e5e7eb', paddingTop: '1rem', fontSize: '0.9rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}><span>Food ({d.expected_pax} x {formatCurrency(d.price_per_pax)})</span><span>{formatCurrency(d.food_total)}</span></div>
            {Number(d.addon_total) > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}><span>Add-ons</span><span>{formatCurrency(d.addon_total)}</span></div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontWeight: 600 }}><span>Subtotal</span><span>{formatCurrency(d.subtotal)}</span></div>
            {Number(d.discount_amount) > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', color: '#dc2626' }}><span>Discount</span><span>-{formatCurrency(d.discount_amount)}</span></div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}><span>Tax ({d.tax_percent}%)</span><span>{formatCurrency(d.tax_amount)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1.1rem', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '2px solid #1e293b' }}><span>Grand Total</span><span>{formatCurrency(d.grand_total)}</span></div>
          </div>
        </div>

        {/* Print Preview Modal */}
        {showPrint && (
          <div className="print-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div style={{ background: '#fff', borderRadius: '12px', maxWidth: '800px', width: '100%', maxHeight: '95vh', overflow: 'auto', position: 'relative' }}>
              {/* Close + Print buttons (hidden on print) */}
              <div className="no-print" style={{ position: 'sticky', top: 0, background: '#fff', padding: '0.75rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10, borderRadius: '12px 12px 0 0' }}>
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Quotation Preview</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button style={btnPrimary} onClick={() => { const el = document.getElementById('quotation-print-area'); const w = window.open('', '', 'width=800,height=900'); w.document.write('<html><head><title>Quotation ' + d.quotation_no + '</title><style>body{font-family:Arial,sans-serif;margin:0;padding:20px;color:#1e293b}table{width:100%;border-collapse:collapse}th,td{padding:8px 12px;text-align:left;border-bottom:1px solid #e5e7eb}.text-right{text-align:right}.text-center{text-align:center}.font-bold{font-weight:700}.text-sm{font-size:0.85rem}.text-xs{font-size:0.75rem}.mt-4{margin-top:1rem}.mb-2{margin-bottom:0.5rem}.border-t{border-top:2px solid #1e293b}.veg-dot{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:6px}</style></head><body>'); w.document.write(el.innerHTML); w.document.write('</body></html>'); w.document.close(); w.print(); }}>Print / PDF</button>
                  <button style={btnOutline} onClick={() => setShowPrint(false)}>Close</button>
                </div>
              </div>

              {/* Printable content */}
              <div id="quotation-print-area" style={{ padding: '2rem 2.5rem' }}>
                {/* Hotel Header */}
                <div style={{ textAlign: 'center', marginBottom: '1.5rem', borderBottom: '3px solid #1e293b', paddingBottom: '1rem' }}>
                  <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: '0 0 0.2rem', color: '#1e293b', letterSpacing: '1px' }}>
                    {hotelInfo?.billing_company_name || selectedProperty?.name || 'HOTEL NAME'}
                  </h1>
                  <p style={{ fontSize: '0.82rem', color: '#475569', margin: '0 0 0.15rem' }}>
                    {[hotelInfo?.address_line1, hotelInfo?.address_line2, hotelInfo?.city, hotelInfo?.state, hotelInfo?.pin_code].filter(Boolean).join(', ') || ''}
                  </p>
                  <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>
                    {hotelInfo?.contact_phone ? `Tel: ${hotelInfo.contact_phone}` : ''}
                    {hotelInfo?.contact_email ? ` | ${hotelInfo.contact_email}` : ''}
                    {hotelInfo?.gstin_number ? ` | GSTIN: ${hotelInfo.gstin_number}` : ''}
                  </p>
                </div>

                {/* Quotation Title */}
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 0.3rem', textTransform: 'uppercase', letterSpacing: '2px', color: '#334155' }}>Banquet Quotation</h2>
                  <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
                    {d.quotation_no} | Date: {formatDate(d.created_at)}
                  </p>
                </div>

                {/* Customer + Event Details */}
                <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.5rem', fontSize: '0.88rem' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Bill To</p>
                    <p style={{ fontWeight: 600, margin: '0 0 0.15rem' }}>{d.customer_name}</p>
                    {d.customer_phone && <p style={{ margin: '0 0 0.1rem', color: '#475569' }}>{d.customer_phone}</p>}
                    {d.customer_email && <p style={{ margin: 0, color: '#475569' }}>{d.customer_email}</p>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Event Details</p>
                    <p style={{ fontWeight: 600, margin: '0 0 0.15rem' }}>{d.event_name || '-'}</p>
                    <p style={{ margin: '0 0 0.1rem', color: '#475569' }}>Date: {formatDate(d.event_start_date)}{d.event_time ? ` | ${String(d.event_time).slice(0,5)}` : ''}{d.event_end_time ? ` - ${String(d.event_end_time).slice(0,5)}` : ''}</p>
                    {d.venue && <p style={{ margin: '0 0 0.1rem', color: '#475569' }}>Venue: {d.venue}</p>}
                    <p style={{ margin: 0, color: '#475569' }}>Pax: {d.expected_pax} | Package: {d.package_name || '-'}</p>
                  </div>
                </div>

                {/* Menu Items */}
                {(d.menu_selections || []).length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <p style={{ fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', color: '#334155', marginBottom: '0.5rem', borderBottom: '1px solid #cbd5e1', paddingBottom: '0.3rem' }}>Menu</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                      {categories.map(cat => {
                        const items = (d.menu_selections || []).filter(s => String(s.category_id) === String(cat.category_id));
                        if (!items.length) return null;
                        return (
                          <div key={cat.category_id}>
                            <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.25rem' }}>{cat.category_name}</p>
                            {items.map(i => (
                              <p key={i.item_id} style={{ fontSize: '0.85rem', margin: '0 0 0.15rem', paddingLeft: '0.2rem' }}>
                                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: i.food_type === 'VEG' ? '#16a34a' : '#dc2626', marginRight: '0.4rem' }}></span>
                                {i.item_name}
                              </p>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Add-ons */}
                {(d.addons || []).length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <p style={{ fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', color: '#334155', marginBottom: '0.5rem', borderBottom: '1px solid #cbd5e1', paddingBottom: '0.3rem' }}>Add-ons</p>
                    <table style={{ width: '100%', fontSize: '0.85rem' }}>
                      <tbody>
                        {d.addons.map((a, i) => (
                          <tr key={i}>
                            <td style={{ padding: '0.3rem 0' }}>{a.addon_name}</td>
                            <td style={{ padding: '0.3rem 0', color: '#6b7280', textAlign: 'center' }}>{a.rate_type === 'PER_PAX' ? `${d.expected_pax} x ₹${a.rate}` : `${a.quantity} x ₹${a.rate}`}</td>
                            <td style={{ padding: '0.3rem 0', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(a.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pricing */}
                <div style={{ borderTop: '2px solid #334155', paddingTop: '1rem', marginBottom: '1.5rem' }}>
                  <p style={{ fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', color: '#334155', marginBottom: '0.5rem' }}>Pricing Summary</p>
                  <table style={{ width: '50%', marginLeft: 'auto', fontSize: '0.9rem' }}>
                    <tbody>
                      <tr><td style={{ padding: '0.3rem 0' }}>Food ({d.expected_pax} pax x {formatCurrency(d.price_per_pax)})</td><td style={{ textAlign: 'right' }}>{formatCurrency(d.food_total)}</td></tr>
                      {Number(d.addon_total) > 0 && <tr><td style={{ padding: '0.3rem 0' }}>Add-ons</td><td style={{ textAlign: 'right' }}>{formatCurrency(d.addon_total)}</td></tr>}
                      <tr style={{ fontWeight: 600 }}><td style={{ padding: '0.3rem 0' }}>Subtotal</td><td style={{ textAlign: 'right' }}>{formatCurrency(d.subtotal)}</td></tr>
                      {Number(d.discount_amount) > 0 && <tr style={{ color: '#dc2626' }}><td style={{ padding: '0.3rem 0' }}>Discount</td><td style={{ textAlign: 'right' }}>-{formatCurrency(d.discount_amount)}</td></tr>}
                      <tr><td style={{ padding: '0.3rem 0' }}>Tax ({d.tax_percent}%)</td><td style={{ textAlign: 'right' }}>{formatCurrency(d.tax_amount)}</td></tr>
                      <tr style={{ fontWeight: 700, fontSize: '1.05rem', borderTop: '2px solid #1e293b' }}>
                        <td style={{ padding: '0.5rem 0' }}>Grand Total</td><td style={{ textAlign: 'right', padding: '0.5rem 0' }}>{formatCurrency(d.grand_total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Notes */}
                {d.notes && (
                  <div style={{ marginBottom: '1.5rem', fontSize: '0.85rem' }}>
                    <p style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Notes:</p>
                    <p style={{ color: '#475569' }}>{d.notes}</p>
                  </div>
                )}

                {/* Terms & Signature */}
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', fontSize: '0.8rem', color: '#64748b' }}>
                  <p style={{ fontWeight: 600, marginBottom: '0.3rem', color: '#475569' }}>Terms & Conditions:</p>
                  <ul style={{ margin: '0 0 1.5rem', paddingLeft: '1.2rem' }}>
                    <li>This quotation is valid for 7 days from the date of issue.</li>
                    <li>Advance payment of 30% is required to confirm the booking.</li>
                    <li>Menu items are subject to availability.</li>
                    <li>Prices are inclusive of service charges. Taxes as applicable.</li>
                  </ul>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3rem' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ borderTop: '1px solid #94a3b8', width: '180px', marginBottom: '0.3rem' }}></div>
                      <span style={{ fontSize: '0.78rem' }}>Customer Signature</span>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ borderTop: '1px solid #94a3b8', width: '180px', marginBottom: '0.3rem' }}></div>
                      <span style={{ fontSize: '0.78rem' }}>Authorized Signature</span>
                    </div>
                  </div>
                  <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.72rem', color: '#94a3b8' }}>© 2026 EASEPMS - Generated on {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Approve & Convert to Booking Modal */}
        {showApproveModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '450px' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem' }}>Convert to Booking</h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>Select hall and layout to create a booking from this quotation.</p>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Hall *</label>
                <select style={inputStyle} value={approveHallId} onChange={e => { setApproveHallId(e.target.value); setApproveLayoutId(''); loadApproveLayouts(e.target.value); }}>
                  <option value="">Select hall...</option>
                  {halls.map(h => <option key={h.hall_id} value={h.hall_id}>{h.hall_name} (Cap: {h.max_capacity})</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Layout *</label>
                <select style={inputStyle} value={approveLayoutId} onChange={e => setApproveLayoutId(e.target.value)}>
                  <option value="">Select layout...</option>
                  {approveLayouts.map(l => <option key={l.layout_id} value={l.layout_id}>{l.layout_name} (Cap: {l.layout_capacity})</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button style={btnOutline} onClick={() => setShowApproveModal(false)}>Cancel</button>
                <button style={{ ...btnPrimary, background: 'linear-gradient(135deg, #ea580c, #c2410c)' }} onClick={approveQuotation}>Approve & Create Booking</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ──── WIZARD VIEW ────
  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <button style={{ ...btnOutline, marginBottom: '1rem' }} onClick={() => setView('list')}>← Back to List</button>
      {message && <div style={{ padding: '0.5rem 1rem', background: '#dcfce7', color: '#166534', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>{message}</div>}
      {error && <div style={{ padding: '0.5rem 1rem', background: '#fef2f2', color: '#991b1b', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>}

      {/* Stepper */}
      <div style={{ display: 'flex', gap: '3px', marginBottom: '1.5rem', background: '#f1f5f9', borderRadius: '12px', padding: '4px', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)' }}>
        {STEPS.map((s, i) => (
          <div key={i} onClick={() => setStep(i)} style={{
            flex: 1, textAlign: 'center', padding: '0.7rem 0.5rem', cursor: 'pointer',
            background: i === step ? 'linear-gradient(135deg, #2563eb, #1d4ed8)' : i < step ? '#dcfce7' : 'transparent',
            color: i === step ? '#fff' : i < step ? '#166534' : '#94a3b8',
            fontWeight: i === step ? 700 : 500, fontSize: '0.8rem',
            borderRadius: '10px', transition: 'all 0.25s ease',
            boxShadow: i === step ? '0 2px 8px rgba(37,99,235,0.35)' : 'none'
          }}>{i < step ? '✓ ' : `${i + 1}. `}{s}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 320px', gap: '1.5rem', alignItems: 'start' }}>
        {/* LEFT: Step Content */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: isMobile ? '1rem' : '1.75rem', boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>

          {/* Step 1: Customer & Event */}
          {step === 0 && (
            <div>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '1rem' }}>Customer & Event Details</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Customer Name *</label><input style={inputStyle} value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} /></div>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Mobile</label><input style={inputStyle} value={form.customer_mobile} onChange={e => setForm({ ...form, customer_mobile: e.target.value })} /></div>
                <div style={{ gridColumn: '1 / -1' }}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Email</label><input style={inputStyle} value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} /></div>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Event Name</label><input style={inputStyle} value={form.event_name} onChange={e => setForm({ ...form, event_name: e.target.value })} placeholder="e.g. Birthday Party" /></div>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Venue / Hall</label>
                  <select style={inputStyle} value={form.venue} onChange={e => setForm({ ...form, venue: e.target.value })}>
                    <option value="">Select hall...</option>
                    {halls.map(h => <option key={h.hall_id} value={h.hall_name}>{h.hall_name} (Cap: {h.max_capacity})</option>)}
                  </select></div>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Event Date *</label><input style={inputStyle} type="date" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value, event_end_date: form.event_end_date || e.target.value })} /></div>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>End Date</label><input style={inputStyle} type="date" value={form.event_end_date} onChange={e => setForm({ ...form, event_end_date: e.target.value })} /></div>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>From Time</label><input style={inputStyle} type="time" value={form.event_from_time} onChange={e => setForm({ ...form, event_from_time: e.target.value })} /></div>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>To Time</label><input style={inputStyle} type="time" value={form.event_to_time} onChange={e => setForm({ ...form, event_to_time: e.target.value })} /></div>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Pax Count *</label><input style={inputStyle} type="number" value={form.pax_count} onChange={e => setForm({ ...form, pax_count: e.target.value })} placeholder="100" /></div>
              </div>
              <div style={{ marginTop: '1rem' }}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Notes</label><textarea style={{ ...inputStyle, minHeight: '60px' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
          )}

          {/* Step 2: Package Selection */}
          {step === 1 && (
            <div>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '1rem' }}>Select Package</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Package *</label>
                  <select style={inputStyle} value={selectedPackageId} onChange={e => setSelectedPackageId(e.target.value)}>
                    <option value="">Select package...</option>
                    {packages.map(p => <option key={p.package_id} value={p.package_id}>{p.package_name} ({p.menu_type === 'NON_VEG' ? 'Non-Veg' : p.menu_type === 'MIXED' ? 'Mixed' : 'Veg'})</option>)}
                  </select>
                </div>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Price Per Pax</label>
                  <input style={inputStyle} type="number" value={pricePerPax} onChange={e => setPricePerPax(e.target.value)} />
                </div>
              </div>
              {packageFull && (
                <div>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem', color: '#475569' }}>Package Rules</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {(packageFull.rules || []).map(r => (
                      <div key={r.rule_id || r.category_id} style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.82rem' }}>
                        <span style={{ fontWeight: 600 }}>{r.category_name}</span>: Select {r.min_select}{r.max_select > r.min_select ? `-${r.max_select}` : ''} items
                      </div>
                    ))}
                  </div>
                  {packageFull.description && <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>{packageFull.description}</p>}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Menu Builder */}
          {step === 2 && (
            <div>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '1rem' }}>Select Menu Items</h3>
              {!packageFull ? <p style={{ color: '#9ca3af' }}>Please select a package first (Step 2).</p> :
                categories.map(cat => {
                  const allowedItems = (packageFull.items || []).filter(i => String(i.category_id) === String(cat.category_id));
                  if (!allowedItems.length) return null;
                  const rule = (packageFull.rules || []).find(r => String(r.category_id) === String(cat.category_id));
                  const selectedCount = menuSelections.filter(s => String(s.category_id) === String(cat.category_id)).length;
                  const isValid = !rule || ((!rule.min_select || selectedCount >= rule.min_select) && (!rule.max_select || selectedCount <= rule.max_select));
                  return (
                    <div key={cat.category_id} style={{ border: `1px solid ${isValid ? '#e5e7eb' : '#fca5a5'}`, borderRadius: '8px', padding: '1rem', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{cat.category_name}</span>
                        <span style={{ fontSize: '0.78rem', color: isValid ? '#6b7280' : '#dc2626' }}>
                          {rule ? `Select ${rule.min_select}${rule.max_select > rule.min_select ? `-${rule.max_select}` : ''} — ` : ''}
                          {selectedCount} selected
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                        {allowedItems.map(item => {
                          const isSelected = menuSelections.some(s => String(s.item_id) === String(item.item_id));
                          return (
                            <div key={item.item_id} onClick={() => toggleMenuItem(item)} style={{
                              display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.8rem',
                              border: `2px solid ${isSelected ? '#2563eb' : '#d1d5db'}`, borderRadius: '20px',
                              background: isSelected ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '0.82rem',
                              transition: 'all 0.15s'
                            }}>
                              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.food_type === 'VEG' ? '#16a34a' : '#dc2626' }}></span>
                              <span>{item.item_name}</span>
                              {isSelected && <span style={{ color: '#2563eb', fontWeight: 700 }}>✓</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              }
            </div>
          )}

          {/* Step 4: Add-ons & Pricing */}
          {step === 3 && (
            <div>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '1rem' }}>Add-ons & Pricing</h3>
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem' }}>Add-ons</h4>
                {addons.map((a, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input style={{ ...inputStyle, flex: 1, minWidth: '150px' }} placeholder="Name (e.g. Welcome Drink)" value={a.addon_name} onChange={e => { const u = [...addons]; u[idx].addon_name = e.target.value; setAddons(u); }} />
                    <select style={{ ...inputStyle, width: '110px' }} value={a.rate_type} onChange={e => { const u = [...addons]; u[idx].rate_type = e.target.value; setAddons(u); }}>
                      <option value="PER_PAX">Per Pax</option>
                      <option value="FIXED">Fixed</option>
                    </select>
                    <input style={{ ...inputStyle, width: '90px' }} type="number" placeholder="Rate" value={a.rate} onChange={e => { const u = [...addons]; u[idx].rate = e.target.value; setAddons(u); }} />
                    {a.rate_type === 'FIXED' && <input style={{ ...inputStyle, width: '70px' }} type="number" placeholder="Qty" value={a.quantity} onChange={e => { const u = [...addons]; u[idx].quantity = e.target.value; setAddons(u); }} />}
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, minWidth: '80px', textAlign: 'right' }}>{formatCurrency(a.rate_type === 'PER_PAX' ? Number(a.rate || 0) * pax : Number(a.rate || 0) * Number(a.quantity || 1))}</span>
                    <button style={{ ...btnOutline, color: '#dc2626', padding: '0.3rem 0.5rem' }} onClick={() => setAddons(addons.filter((_, i) => i !== idx))}>✕</button>
                  </div>
                ))}
                <button style={{ ...btnOutline, color: '#2563eb' }} onClick={() => setAddons([...addons, { addon_name: '', rate_type: 'PER_PAX', rate: '', quantity: 1 }])}>+ Add Item</button>
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem' }}>Discount & Tax</h4>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ width: '130px' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>Discount Type</label>
                    <select style={inputStyle} value={discountType} onChange={e => setDiscountType(e.target.value)}>
                      <option value="">None</option>
                      <option value="FIXED">Fixed (₹)</option>
                      <option value="PERCENT">Percent (%)</option>
                    </select>
                  </div>
                  {discountType && <div style={{ width: '120px' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>Discount Value</label>
                    <input style={inputStyle} type="number" value={discountValue} onChange={e => setDiscountValue(e.target.value)} />
                  </div>}
                  <div style={{ width: '120px' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>Tax %</label>
                    <input style={inputStyle} type="number" value={taxPercent} onChange={e => setTaxPercent(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {step === 4 && (
            <div>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '1rem' }}>Review & Save</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.85rem', marginBottom: '1rem' }}>
                <div><p style={{ color: '#6b7280', fontSize: '0.75rem' }}>Customer</p><p style={{ fontWeight: 600 }}>{form.customer_name || '-'}</p>{form.customer_mobile && <p>{form.customer_mobile}</p>}{form.customer_email && <p>{form.customer_email}</p>}</div>
                <div><p style={{ color: '#6b7280', fontSize: '0.75rem' }}>Event</p><p style={{ fontWeight: 600 }}>{form.event_name || '-'}</p><p>{formatDate(form.event_date)}{form.event_from_time ? ` | ${form.event_from_time}` : ''}{form.event_to_time ? ` - ${form.event_to_time}` : ''}</p>{form.venue && <p>{form.venue}</p>}<p>Pax: {pax}</p></div>
              </div>
              <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Package: {packageFull?.package_name || '-'} | Price/Pax: {formatCurrency(ppPax)}</p>
              {menuSelections.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem', marginBottom: '1rem' }}>
                  {categories.map(cat => {
                    const items = menuSelections.filter(s => String(s.category_id) === String(cat.category_id));
                    if (!items.length) return null;
                    return (
                      <div key={cat.category_id} style={{ background: '#f8fafc', borderRadius: '8px', padding: '0.6rem' }}>
                        <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.2rem' }}>{cat.category_name}</p>
                        {items.map(i => <p key={i.item_id} style={{ fontSize: '0.82rem' }}><span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: i.food_type === 'VEG' ? '#16a34a' : '#dc2626', marginRight: '0.4rem' }}></span>{i.item_name}</p>)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Navigation buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
            <button style={btnOutline} onClick={() => step > 0 ? setStep(step - 1) : setView('list')} disabled={loading}>{step === 0 ? 'Cancel' : '← Back'}</button>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {step < 4 && <button style={btnPrimary} onClick={() => setStep(step + 1)}>Next →</button>}
              {step === 4 && <button style={btnSuccess} onClick={saveQuotation} disabled={loading}>{editingId ? 'Update Quotation' : 'Save Quotation'}</button>}
            </div>
          </div>
        </div>

        {/* RIGHT: Summary Panel */}
        <SummaryPanel />
      </div>
    </div>
  );
}

export default BanquetQuotationV2;
